import queryparse from 'tricks/string/queryparse';
import querystringify from 'tricks/string/querystringify';
const DEFAULT_ITEMS_PER_PAGE = 100;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_QUERY_PARAM = 'page';

/**
 * Generate a url with pagination details
 * @param {object} options - Options
 * @param {object} options.location - react-router location object. For more details, please check https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/location.md
 * @param {number} options.page - Page number
 * @param {string} [options.pageQueryParameter="page"] - Name of the query parameter that we need to use to specify the page number
 * @returns {object} The current location with the target page query and state for resetting scroll position added to it
 */
function locationWithPageNumber({
	location,
	page,
	pageQueryParameter = DEFAULT_PAGE_QUERY_PARAM,
}) {
	const query = queryparse(location.search);
	const updatedQueryParams = {...query, [pageQueryParameter]: page};

	return {
		...location,
		search: `?${querystringify(updatedQueryParams)}`,
		state: {
			...location.state,
			shouldResetScroll: true,
		},
	};
}

/**
 * Get the current page number from the query parameters
 * @param {object} options - Options
 * @param {object} options.query - Query object, extracted from react-router's location object
 * @param {string} [options.pageQueryParameter="page"] - Name of the query parameter that we need to use to specify the page number
 * @returns {number} Current page number
 */
function getPageFromQuery({
	query,
	pageQueryParameter = DEFAULT_PAGE_QUERY_PARAM,
}) {
	const page = query[pageQueryParameter];

	return Number.isNaN(+page) ? DEFAULT_PAGE_NUMBER : +page;
}

/**
 * Get the total number of pages from the total number of items we can display
 * @param {object} options - Options
 * @param {number} [options.total] - Total number of items to display
 * @param {number} [options.itemsPerPage=100] - Maximum number of items par page
 * @returns {number} Number of pages
 */
function getNumberOfPages({total, itemsPerPage = DEFAULT_ITEMS_PER_PAGE}) {
	return total
		? Math.floor((total - 1) / itemsPerPage) + 1
		: DEFAULT_PAGE_NUMBER;
}

/**
 * Get offset to use when requesting data for a particular page
 * @param {object} options - Options
 * @param {number} options.page - Number of the page we want to retrieve
 * @param {number} [options.itemsPerPage=100] - Maximum number of items par page
 * @returns {number} Items search offset
 */
function getPageOffset({page, itemsPerPage = DEFAULT_ITEMS_PER_PAGE}) {
	return (page - 1) * itemsPerPage; // the start/offset is 0-based
}

export {
	locationWithPageNumber,
	getPageFromQuery,
	getNumberOfPages,
	getPageOffset,
	DEFAULT_ITEMS_PER_PAGE,
	DEFAULT_PAGE_NUMBER,
	DEFAULT_PAGE_QUERY_PARAM,
};
